import React, { useRef, useState, useEffect, Suspense } from "react";
import { Outlet, useLocation, NavLink } from "react-router-dom";
import { Box, AppBar, Toolbar, Typography, Avatar, Button, Popover, MenuItem } from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import SettingsIcon from "@mui/icons-material/Settings";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import MedicationLiquidIcon from "@mui/icons-material/MedicationLiquid";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GridViewIcon from "@mui/icons-material/GridView";
import AssessmentIconcon from "@mui/icons-material/Assessment";
import { useDispatch, useSelector } from "react-redux";
import { fetchNhanVien } from "./redux/slices/nhanVienSlice";
import { fetchPhongBan } from "./redux/slices/phongBanSlice";
import { fetchListCLS } from "./redux/slices/clsSlice";
import { fetchProvinces, fetchWards } from "./redux/slices/caidatSlice";

// Hàm lấy danh sách company từ localStorage
function getCompanyList() {
  try {
    const raw = localStorage.getItem("company");
    if (!raw) return [];
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export default function MainLayout() {
  const dispatch = useDispatch();
  // State cho chi nhánh
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const { listCLS } = useSelector((state) => state.cls);

  useEffect(() => {
    const list = getCompanyList();
    setCompanyList(list);
    // Lấy operating_unit_id đã chọn trước đó
    const saved = localStorage.getItem("selected_operating_unit_id");
    if (saved && list.some((c) => c.id === Number(saved))) {
      setSelectedCompany(Number(saved));
      const selectedUnit = list.find((c) => c.id === Number(saved));
      if (selectedUnit) {
        localStorage.setItem("selected_operating_unit", JSON.stringify(selectedUnit));
      }
    } else if (list.length > 0) {
      setSelectedCompany(list[0].id);
      localStorage.setItem("selected_operating_unit_id", list[0].id);
      localStorage.setItem("selected_operating_unit", JSON.stringify(list[0]));
      localStorage.setItem("selected_company_name", list[0].name);
    }
  }, []);

  useEffect(() => {
    // Fetch list nhân viên một lần cho toàn hệ thống
    dispatch(fetchNhanVien());
    // Fetch danh sách phòng ban một lần cho toàn hệ thống
    dispatch(fetchPhongBan());
    // Fetch list CLS từ API
    dispatch(fetchListCLS());
    // Fetch danh sách tỉnh và xã/phường Việt Nam một lần
    dispatch(fetchProvinces());
    dispatch(fetchWards());
    // ...existing code (other global fetches nếu có)
  }, [dispatch]);

  const handleChangeCompany = (e) => {
    const newId = Number(e.target.value);
    setSelectedCompany(newId);
    localStorage.setItem("selected_operating_unit_id", e.target.value);
    const selectedUnit = companyList.find((c) => c.id === newId);
    if (selectedUnit) {
      localStorage.setItem("selected_operating_unit", JSON.stringify(selectedUnit));
    }
    window.location.reload(); // reload để áp dụng chi nhánh mới
  };
  const location = useLocation();

  const [openedMenu, setOpenedMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRefs = useRef({});

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const handleUserMenuOpen = (e) => setUserMenuAnchor(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    window.location.href = "/";
    setTimeout(() => window.location.reload(), 100);
  };

  // Khi click vào menu cha, show/hide submenu
  const handleMenuClick = (key, el) => {
    if (openedMenu === key) {
      setOpenedMenu(null);
      setAnchorEl(null);
    } else {
      setOpenedMenu(key);
      setAnchorEl(el);
    }
  };

  // Đọc danh sách mã quyền từ localStorage
  function getPermissionCodes() {
    try {
      const raw = localStorage.getItem("permissions_codes");
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  const permissionCodes = getPermissionCodes();

  // Hàm bỏ dấu tiếng Việt
  const removeVietnameseTones = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D");
  };

  // Bản đồ: Tên menu (tiếng Việt) -> Mã quyền (code từ backend)
  const PERM_BY_LABEL = {
    "Tổng quan": "TRANG_TONG_QUAN",
    "Tiếp đón": "TRANG_TIEP_DON",
    "Khám bệnh": "TRANG_KHAM_BENH_KHAM_BENH",
    "Đơn thuốc": "TRANG_KHAM_BENH_DON_THUOC",
    "Khách hàng": "TRANG_KHACH_HANG",
    "Nhóm khách hàng": "TRANG_KHACH_HANG",
    "Cận lâm sàng": "TRANG_CAN_LAM_SANG",
    "Kế Toán": "TRANG_KE_TOAN",
    "Thanh toán": "TRANG_KE_TOAN_THANH_TOAN",
    "Hóa đơn": "TRANG_KE_TOAN_HOA_DON",
    "Hệ Thống": "TRANG_HE_THONG",
    "Nhóm hàng hóa": "TRANG_HE_THONG_NHOM_HANG_HOA",
    "Hàng hóa": "TRANG_HE_THONG_HANG_HOA_DICH_VU",
    "Dịch vụ": "TRANG_HE_THONG_HANG_HOA_DICH_VU",
    "Nhân viên": "TRANG_HE_THONG_NHAN_VIEN",
    "Phòng ban": "TRANG_HE_THONG_PHONG_BAN",
    "Vai trò": "TRANG_HE_THONG_VAI_TRO",
    "Liên thông": "TRANG_HE_THONG_LIEN_THONG",
    "Cài đặt": "TRANG_HE_THONG_CAI_DAT",
    // Thêm CLS mapping động
    ...listCLS.reduce((acc, item) => {
      const normalizedName = removeVietnameseTones(item.display_name).toUpperCase().replace(/\s+/g, "_").replace(/-/g, "_");
      acc[item.display_name] = `TRANG_CAN_LAM_SANG_${normalizedName}`;
      return acc;
    }, {}),
  };

  // Kiểm tra quyền: Nếu labelText có mã quyền tương ứng và user có quyền đó thì trả về true
  const hasPerm = (labelText) => {
    if (!labelText) return true;
    const code = PERM_BY_LABEL[labelText];
    if (!code) return true; // Chưa định nghĩa code thì mặc định hiển thị
    // Tổng quan luôn hiển thị cho tất cả user
    if (labelText === "Tổng quan" || code === "TRANG_TONG_QUAN") return true;

    // // Kiểm tra nếu labelText là một trong các submenu của Hệ Thống
    // const heThongSubmenus = ["Nhóm hàng hóa", "Hàng hóa", "Dịch vụ", "Nhân viên", "Phòng ban", "Vai trò", "Liên thông", "Cài đặt"];
    // if (heThongSubmenus.includes(labelText)) {
    //   // Nếu có quyền cho submenu này (trực tiếp hoặc có prefix), tự động cho phép hiển thị
    //   if (permissionCodes.includes(code) || permissionCodes.some((perm) => perm.startsWith(code + "_"))) return true;
    // }

    // // Hệ Thống: hiển thị nếu có bất kỳ quyền con nào
    // if (labelText === "Hệ Thống") {
    //   const hasAnyHeThongPerm = heThongSubmenus.some((submenuLabel) => {
    //     const submenuCode = PERM_BY_LABEL[submenuLabel];
    //     return submenuCode && (permissionCodes.includes(submenuCode) || permissionCodes.some((perm) => perm.startsWith(submenuCode + "_")));
    //   });
    //   if (hasAnyHeThongPerm) return true;
    // }

    // // Kiểm tra permission trực tiếp
    // if (permissionCodes.includes(code)) return true;

    // Nếu là menu cha, kiểm tra xem có bất kỳ permission con nào không
    // Ví dụ: TRANG_KE_TOAN sẽ check TRANG_KE_TOAN_THU_CHI, TRANG_KE_TOAN_CONG_NO,...
    const hasChildPerm = permissionCodes.some((perm) => perm.startsWith(code));
    return hasChildPerm;
  };

  const menuItems = [
    {
      key: "tong-quan",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <GridViewIcon fontSize="small" /> Tổng quan
        </span>
      ),
      label: "Tổng quan",
      to: "/tong-quan",
    },
    {
      key: "tiep-don",
      label: "Tiếp đón",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <ReceiptIcon fontSize="small" /> Tiếp đón
        </span>
      ),
      to: "/tiep-don",
    },
    {
      key: "kham-benh",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <MedicationLiquidIcon fontSize="small" /> Khám bệnh
        </span>
      ),
      label: "Khám bệnh",
      to: "/kham-benh",
      hasSubMenu: true,
      subMenu: [
        { label: "Khám bệnh", to: "/kham-benh" },
        { label: "Đơn thuốc", to: "/kham-benh/don-thuoc" },
      ],
    },

    {
      key: "khach-hang",
      label: "Khách hàng",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <SupervisorAccountIcon fontSize="small" /> Khách hàng
        </span>
      ),
      to: "/kham-benh",
      hasSubMenu: true,
      subMenu: [
        { label: "Khách hàng", to: "/khach-hang" },
        { label: "Nhóm khách hàng", to: "/khach-hang/nhom-khach-hang" },
      ],
    },
    {
      key: "can-lam-sang",
      label: "Cận lâm sàng",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <MonitorHeartIcon fontSize="small" /> Cận lâm sàng
        </span>
      ),
      to: "/can-lam-sang",
      hasSubMenu: true,
      subMenu: listCLS.map((item) => ({ label: item.display_name, to: `/can-lam-sang/${item.id}` })),
    },
    // {
    //   key: "nha-thuoc",
    //   content: (
    //     <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
    //       <MedicalServicesIcon fontSize="small" /> Nhà thuốc
    //     </span>
    //   ),
    //   to: "/nha-thuoc",
    // },
    // {
    //   key: "quan-ly-kho",
    //   content: (
    //     <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
    //       <StoreIcon fontSize="small" /> Quản lý kho
    //     </span>
    //   ),
    //   to: "/quan-ly-kho",
    // },
    {
      key: "ke-toan",
      label: "Kế Toán",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <CalculateIcon fontSize="small" /> Kế Toán
        </span>
      ),
      to: "/ke-toan",
      hasSubMenu: true,
      subMenu: [
        { label: "Thanh toán", to: "/ke-toan/thanh-toan" },
        { label: "Hóa đơn", to: "/ke-toan/hoa-don" },
      ],
    },
    {
      key: "bao-cao",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <AssessmentIconcon fontSize="small" /> Báo Cáo
        </span>
      ),
      to: "/bao-cao",
      hasSubMenu: true,
      subMenu: [
        { label: "Báo cáo Khám", to: "/bao-cao/kham" },
        { label: "Báo cáo Khách hàng", to: "/bao-cao/khach-hang" },
        { label: "Báo cáo Bán hàng", to: "/bao-cao/ban-hang" },
        { label: "Báo cáo Bác sĩ", to: "/bao-cao/bac-si" },
        { label: "Báo cáo Người giới thiệu", to: "/bao-cao/nguoi-gioi-thieu" },
        { label: "Báo cáo Người thực hiện", to: "/bao-cao/nguoi-thuc-hien" },
      ],
    },
    {
      key: "he-thong",
      label: "Hệ Thống",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <SettingsIcon fontSize="small" /> Hệ Thống
        </span>
      ),
      to: "/he-thong",
      hasSubMenu: true,
      subMenu: [
        { label: "Nhóm hàng hóa", to: "/he-thong/nhom-hang-hoa" },
        { label: "Hàng hóa", to: "/he-thong/hang-hoa" },
        { label: "Dịch vụ", to: "/he-thong/dich-vu" },
        { label: "Nhân viên", to: "/he-thong/nhan-vien" },
        { label: "Phòng ban", to: "/he-thong/phong-ban" },
        { label: "Vai trò", to: "/he-thong/vai-tro" },
        { label: "Liên thông", to: "/he-thong/lien-thong" },
        { label: "Cài đặt", to: "/he-thong/cai-dat" },
      ],
    },
  ];

  return (
    <Box bgcolor="#f8fbfc" minHeight="100vh">
      <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: "#fff" }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <img loading="lazy" src={process.env.PUBLIC_URL + "/favicon.png"} alt="logo" style={{ height: 40, marginRight: 12 }} width={40} height={40} />
          <Typography variant="h6" color="#00897b" sx={{ fontWeight: 700, flexGrow: 1 }}>
            PHÒNG KHÁM CAO CẤP
          </Typography>
          {/* Lựa chọn chi nhánh */}
          {companyList.length > 0 && (
            <Box sx={{ minWidth: 220, mr: 2 }}>
              <select
                value={selectedCompany || ""}
                onChange={handleChangeCompany}
                style={{ padding: 8, borderRadius: 6, fontWeight: 600, fontSize: 15, border: "1px solid #bdbdbd", minWidth: 180 }}
              >
                {companyList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: "#2f9480", mr: 1, cursor: "pointer" }} onClick={handleUserMenuOpen}>
              {(localStorage.getItem("username") || "A").charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" sx={{ cursor: "pointer" }} onClick={handleUserMenuOpen}>
              {localStorage.getItem("username") || "User"}
            </Typography>
            <Popover
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              disableRestoreFocus
            >
              <MenuItem onClick={handleUserMenuClose}>Đổi mật khẩu</MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: "#d32f2f", fontWeight: 700 }}>
                Đăng xuất
              </MenuItem>
            </Popover>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        bgcolor="#2f9480"
        color="#fff"
        px={1}
        py={1}
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          flexWrap: { xs: "wrap", sm: "wrap", md: "nowrap" },
          rowGap: 1,
          "@media (max-width:1400px)": {
            flexWrap: "wrap",
            justifyContent: "flex-start",
          },
        }}
      >
        {menuItems
          .filter((item) => hasPerm(item.label))
          .map((item) =>
            item.hasSubMenu ? (
              <Box key={item.key} sx={{ display: "inline-block", position: "relative" }}>
                <Button
                  ref={(el) => (buttonRefs.current[item.key] = el)}
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    background: location.pathname.startsWith(`/${item.key}`) ? "#1976d2" : "none",
                  }}
                  onClick={(e) => handleMenuClick(item.key, e.currentTarget)}
                >
                  {item.content}
                </Button>
                <Popover
                  open={openedMenu === item.key}
                  anchorEl={anchorEl}
                  onClose={() => setOpenedMenu(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  disableRestoreFocus
                  PaperProps={{
                    sx: { mt: 1, borderRadius: 1, boxShadow: 3, minWidth: 200, p: 0 },
                  }}
                >
                  {item.subMenu
                    .filter((sub) => hasPerm(sub.label))
                    .map((sub, idx) => (
                      <MenuItem
                        key={sub.to}
                        component={NavLink}
                        to={sub.to}
                        onClick={() => setOpenedMenu(null)}
                        selected={location.pathname === sub.to}
                        sx={{
                          fontWeight: 600,
                          color: "#2f9480",
                          fontSize: 16,
                          borderBottom: idx !== item.subMenu.length - 1 ? "1px solid #e0e0e0" : undefined,
                        }}
                      >
                        {sub.label}
                      </MenuItem>
                    ))}
                </Popover>
              </Box>
            ) : (
              <Box key={item.key} sx={{ display: "inline-block" }}>
                <Button
                  component={NavLink}
                  to={item.to}
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    background: location.pathname.startsWith(`/${item.key}`) ? "#1976d2" : "none",
                  }}
                >
                  {item.content}
                </Button>
              </Box>
            )
          )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", height: "calc(100vh - 120px)" }} className="main-content">
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </Box>
    </Box>
  );
}
